import React, { FC, useContext }                        from 'react';
import _                                                from 'underscore';
import { Button, Divider, Header, Icon, List, Segment } from 'semantic-ui-react';
import { Cell, Grid }                                   from "styled-css-grid";
import { useHistory }                                   from 'react-router-dom';

import { capitalize, RouterUtility }                          from '../../../../../util';
import { SystemAccountingQueries }                            from '../../../../../queries';
import { CorvalProject, CorvalUIColor, Position, Size, Uuid } from '../../../../../types';
import { useCurrentUser, useFeatureFlags, useProject }        from '../../../../../hooks';
import { TimeService }                                        from '../../../../../services';
import { StyledContainer }                                    from '../../../../containers';
import { HexagonShape }                                       from '../../../../components/util/visual/HexagonShape';
import { LoadingIndicator }                                   from '../../../../components/util/loading/LoadingIndicator';
import { TextLink }                                           from '../../../../components/display/TextLink';
import { OrganizationalUserList }                             from '../../../../components/user/OrganizationalUserList';
import { ProjectDetails }                                     from '../../../../components/from-query/ProjectDetails';
import { ProjectListContext }                                 from './ProjectListIndexPage';
import { RolePolicyType }                                     from '../../../../../services/RoleAccessService';

interface Props {}

const defaultProps: Props = {}

const ProjectListFocusedItem: FC<Props> = ( props ) => {

	let history                                          = useHistory(),
	    { hasProjectRoleAccessTo }                       = useCurrentUser(),
	    { openProject, renameProject, duplicateProject } = useProject(),
	    { focusedProjectId, projectListRequestRef }      = useContext( ProjectListContext ),
	    featureFlags                                     = useFeatureFlags();

	let canUpdateSettings = hasProjectRoleAccessTo( focusedProjectId, [ RolePolicyType.PROJECT_MANAGE_ACCESS ] );

	const handleOpenProject = ( projectId: Uuid ) => {

		openProject( projectId );
	};

	const handleRenameProject = ( projectId: Uuid, projectName: string ) => {

		renameProject( projectId, projectName, async ( data: CorvalProject ) => {

			// refetch project listing
			await projectListRequestRef.current.refetch();

		} );
	};

	const handleDuplicateProject = ( projectId: Uuid, projectName: string ) => {

		duplicateProject( projectId, projectName, async ( data: CorvalProject ) => {

			// add the new id into the search
			projectListRequestRef.current.variables.where.id.in.push( data.id );

			// refetch project listing with new id included
			await projectListRequestRef.current.refetch();

		} );
	};

	const handleContinueDiscovery = ( projectId: Uuid ) => {

		history.push( RouterUtility.getRoute( 'PROJECT.SURVEYS.INQUIRY', { projectId } ) );
	};

	const handleManageProject = ( projectId: Uuid ) => {

		history.push( RouterUtility.getRoute( 'PROJECT.MANAGE.SETTINGS', { projectId } ) );
	};

	return (

		<ProjectDetails
			id={ focusedProjectId }
			get={ `${ SystemAccountingQueries.fragments.PROJECTS.EDIT_FIELDS() } isDiscoveryComplete` }
			loadingComponent={ () => (
				<div className={ 'padding-xl-top' }>
					<LoadingIndicator isActive={ true } dimmer={ false } inverted label={ 'Retrieving Project...' }/>
				</div>
			) }>

			{ ( item: Partial<CorvalProject> ) => {

				let operations = {
					manageProject:    {
						label:    'Settings',
						callback: () => handleManageProject( item.id )
					},
					openProject:      {
						label:    'Open Project',
						callback: () => handleOpenProject( item.id )
					},
					continueInquiry:  {
						label:    'Continue Inquiry',
						callback: () => handleContinueDiscovery( item.id )
					},
					renameProject:    {
						label:    'Rename',
						callback: () => handleRenameProject( item.id, item.name )
					},
					duplicateProject: {
						label:    'Duplicate',
						callback: () => handleDuplicateProject( item.id, item.name )
					},
				}

				let operationsMenuItems = _.compact( [
					( item.isDiscoveryComplete && canUpdateSettings ) ? operations.manageProject : null,
					featureFlags.enableProjectRenamingOrDuplicationFeatures ? operations.renameProject : null,
					featureFlags.enableProjectRenamingOrDuplicationFeatures ? operations.duplicateProject : null,
				] )

				let gatewayOperation = item.isDiscoveryComplete ? operations.openProject : operations.continueInquiry;

				/* TODO: convert labels to dictionary look ups */
				let detailFields: Array<{ label: string, value: string }> = [
					{ label: 'Asset Name', value: item.assetName },
					{ label: 'Asset Indication', value: item.assetIndication },
					{ label: 'Launch Readiness', value: item.launchDateUtc ? TimeService.ISOtoDisplayDate( item.launchDateUtc ) : null },
				];

				return (

					<Segment inverted color={ 'blue' } className={ 'fade-in container--rounded position--relative overflow--hidden' }>

						<StyledContainer zIndex={ 100 } position={ Position.ABSOLUTE } top={ -100 } right={ -100 } className={ 'opacity--25' }>
							<HexagonShape color={ CorvalUIColor.DARKBLUE } size={ 300 } rotation={ 15 }/>
						</StyledContainer>

						<StyledContainer position={ Position.RELATIVE } padding={ Size.MD } zIndex={ 200 }>

							<Grid columns={ `minmax(50%, 1fr) auto` } rowGap={ '10px' }>

								<Cell className={ 'peek-container cursor--pointer position--relative' } style={ { paddingRight: 30, paddingTop: 5 } }>

									<Header as={ 'h2' } inverted>

										{/* Project Name */ }
                                        <span className={'text-capitalize'}>{ item.name }</span>

										{/* Duplication Icon */ }
										{ featureFlags.enableProjectRenamingOrDuplicationFeatures && (

											<span onClick={ operations.renameProject.callback }
											      className={ 'peek-item display--inline-block text--colored-red position--absolute position--right position--top' }
											      style={ { right: 10 } }>

												<Icon name={ 'pencil' } size={ 'small' } fitted/>

											</span>
										) }
									</Header>

								</Cell>
								<Cell>

									<Button primary
									        onClick={ gatewayOperation.callback }
									        content={ gatewayOperation.label }/>

								</Cell>
								<Cell width={ 2 }>

									<List inverted horizontal divided relaxed size={ 'small' }>

										{ operationsMenuItems.map( operationItem => (

											<List.Item key={ operationItem.label }>
												<TextLink as={ 'span' }
												          className={ 'display--inline-block margin-xs-left text--colored-red' }
												          onClick={ operationItem.callback }>
													{ operationItem.label }
												</TextLink>
											</List.Item>

										) ) }

									</List>

								</Cell>

							</Grid>

							<Divider inverted/>

							<List divided relaxed>

								{ detailFields.map( ( detail, index ) => (

									( detail.value ) ? (

										<List.Item key={ index } style={ { borderColor: 'rgba(255,255,255,0.15)' } }>

											<Icon name={ 'cube' } color={ 'teal' } fitted/>
											<List.Content>
												<Grid columns={ `minmax(auto, 30%) auto` }>
													<Cell><strong className={ 'text--colored-teal' }>{ detail.label }:</strong></Cell>
													<Cell>{ detail.value }</Cell>
												</Grid>
											</List.Content>
										</List.Item>

									) : null

								) ) }

							</List>

							<Divider inverted/>

							<Header as={ 'h4' } inverted content={ 'Current Team' }/>

							<OrganizationalUserList
								organizationId={ item.organizationId }
								userIds={ _.pluck( item.projectMembers, 'accountId' ) }
								horizontal={ true }
								columns={ 3 }/>

						</StyledContainer>
					</Segment>
				)
			} }

		</ProjectDetails>
	);
};

ProjectListFocusedItem.defaultProps = defaultProps;

export { ProjectListFocusedItem }
